/**
 * ScheduleLibraryModal — the firm's Master Schedule Library (editable), opened from the Utilities
 * menu (`open-schedule-library`), alongside the Spec Library. Unlike the read-only Spec Library, this
 * one is managed in-app by library managers ('admin' or 'spec-library' module — enforced server-side).
 *
 * Tabs:
 *   - Columns      — per schedule type: its grouping + the column/field definitions that drive the
 *                    Schedule Builder grid (label, kind, which option list, show-in-grid, order).
 *   - Option Lists — the constrained dropdown vocabularies (Appliance Type, finishes, …): add /
 *                    rename / delete / reorder values.
 *   (Catalog tab — standard products to pull into a project — lands with the catalog migration.)
 *
 * Data via services/scheduleMaster.js. Edits write the master tables; the Schedule Builder reads the
 * same config so changes show up there.
 */

import { useEffect, useState, useMemo } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useDarkMode } from '../outline/theme.js'
import { isSeatLoaded, loadCurrentUserSeat } from '../services/tenancy.js'
import {
  getMasterConfig, upsertType, upsertField, deleteField, reorderFields,
  addOption, renameOption, deleteOption, reorderOptions,
  getCatalog, deleteCatalogItem, migrateCatalogBatch,
} from '../services/scheduleMaster.js'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'

const KINDS = ['text', 'textarea', 'select', 'label', 'section', 'room', 'image', 'url', 'date']
const GROUP_BYS = ['none', 'spec_section', 'room_string']
const sans = '"DM Sans",sans-serif', mono = '"DM Mono",monospace', display = '"Montserrat",sans-serif'

export default function ScheduleLibraryModal() {
  const dark = useDarkMode()
  const { getToken } = useAuth()
  const { user } = useUser()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('columns')
  const [cfg, setCfg] = useState(null)   // { types, fields, options }
  const [activeType, setActiveType] = useState(null)
  const [activeSet, setActiveSet] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [catalog, setCatalog] = useState(null)   // catalog items for the active type
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState(null)

  const bg = dark ? '#08191f' : '#f4f7f8'
  const panel = dark ? '#0b1c24' : '#ffffff'
  const border = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.55)'
  const rowBg = dark ? 'rgba(255,255,255,0.03)' : '#f4f7f8'
  const accent = '#07272D'

  function reload() {
    return getMasterConfig().then(setCfg).catch(e => setErr(e.message || String(e)))
  }
  useEffect(() => {
    function onOpen() { setOpen(true) }
    window.addEventListener('open-schedule-library', onOpen)
    return () => window.removeEventListener('open-schedule-library', onOpen)
  }, [])
  useEffect(() => {
    if (!open) return
    window.__clerkGetToken = () => getToken()
    if (!isSeatLoaded() && user?.id) loadCurrentUserSeat(user.id)
    setErr(null); reload()
    document.body.style.overflow = 'hidden'
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id])

  // Derived: types sorted, fields by type, option sets (names from options + any field.option_set).
  const types = useMemo(() => (cfg?.types || []).slice().sort((a, b) => a.sort_order - b.sort_order), [cfg])
  const fieldsByType = useMemo(() => {
    const m = {}; for (const f of (cfg?.fields || [])) (m[f.schedule_type] = m[f.schedule_type] || []).push(f)
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.sort_order - b.sort_order)
    return m
  }, [cfg])
  const optionSets = useMemo(() => {
    const names = new Set()
    for (const o of (cfg?.options || [])) names.add(o.set_name)
    for (const f of (cfg?.fields || [])) if (f.option_set) names.add(f.option_set)
    return [...names].sort()
  }, [cfg])
  const optionsBySet = useMemo(() => {
    const m = {}; for (const o of (cfg?.options || [])) (m[o.set_name] = m[o.set_name] || []).push(o)
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.sort_order - b.sort_order)
    return m
  }, [cfg])

  useEffect(() => { if (open && cfg && !activeType && types.length) setActiveType(types[0].schedule_type) }, [open, cfg, activeType, types])
  useEffect(() => { if (open && cfg && !activeSet && optionSets.length) setActiveSet(optionSets[0]) }, [open, cfg, activeSet, optionSets])

  async function run(fn) { setBusy(true); setErr(null); try { await fn(); await reload() } catch (e) { setErr(e.message || String(e)) } finally { setBusy(false) } }

  // ── Field ops ──
  function editFieldLabel(f) { promptDialog({ title: 'Column label', defaultValue: f.label }).then(v => { if (v != null && v !== f.label) run(() => upsertField({ ...f, label: v })) }) }
  function setFieldKind(f, kind) { run(() => upsertField({ ...f, kind })) }
  function setFieldOptionSet(f, option_set) { run(() => upsertField({ ...f, option_set: option_set || null })) }
  function toggleInTable(f) { run(() => upsertField({ ...f, in_table: !f.in_table })) }
  function moveField(list, i, dir) {
    const j = i + dir; if (j < 0 || j >= list.length) return
    const a = list[i], b = list[j]
    run(() => reorderFields([{ id: a.id, sort_order: b.sort_order }, { id: b.id, sort_order: a.sort_order }]))
  }
  async function removeField(f) {
    if (!(await confirmDialog({ title: 'Delete column?', message: `Remove "${f.label}" from ${f.schedule_type}? Existing item data isn't deleted; the column just stops showing.`, confirmLabel: 'Delete', danger: true }))) return
    run(() => deleteField(f.id))
  }
  async function addField() {
    const label = await promptDialog({ title: 'New column', message: 'Column label (e.g. Vendor)', required: true }); if (!label) return
    const col_key = await promptDialog({ title: 'New column', message: 'Stored field key (the attribute name; use exactly as data is stored)', defaultValue: label, required: true }); if (!col_key) return
    const list = fieldsByType[activeType] || []
    const sort_order = (list.length ? list[list.length - 1].sort_order : 0) + 1
    run(() => upsertField({ schedule_type: activeType, col_key, label, kind: 'text', sort_order, in_table: true, in_editor: true }))
  }
  function setGroupBy(gb) { const t = types.find(x => x.schedule_type === activeType); if (t) run(() => upsertType({ ...t, group_by: gb })) }

  // ── Option ops ──
  async function addVal() { const v = await promptDialog({ title: 'Add option', message: `New value for "${activeSet}"`, required: true }); if (v) run(() => addOption(activeSet, v)) }
  function renameVal(o) { promptDialog({ title: 'Rename option', defaultValue: o.value }).then(v => { if (v && v !== o.value) run(() => renameOption(o.id, v)) }) }
  async function removeVal(o) { if (!(await confirmDialog({ title: 'Delete option?', message: `Remove "${o.value}"?`, confirmLabel: 'Delete', danger: true }))) return; run(() => deleteOption(o.id)) }
  function moveVal(list, i, dir) {
    const j = i + dir; if (j < 0 || j >= list.length) return
    const a = list[i], b = list[j]
    run(() => reorderOptions([{ id: a.id, sort_order: b.sort_order }, { id: b.id, sort_order: a.sort_order }]))
  }
  async function addSet() { const name = await promptDialog({ title: 'New option list', message: 'List name (e.g. Vendor)', required: true }); if (!name) return; const v = await promptDialog({ title: 'First value', message: `First value for "${name}"`, required: true }); if (!v) return; await run(() => addOption(name, v)); setActiveSet(name) }

  // ── Catalog ──
  function loadCatalog() { if (!activeType) return; setCatalog(null); getCatalog(activeType).then(setCatalog).catch(e => { setCatalog([]); setErr(e.message || String(e)) }) }
  useEffect(() => { if (open && tab === 'catalog' && activeType) loadCatalog() /* eslint-disable-next-line */ }, [open, tab, activeType])
  async function doImport() {
    if (!(await confirmDialog({ title: 'Import catalog from Airtable?', message: 'Import the Electrical Fixture and Hardware type templates into the product catalog. Safe to re-run — it upserts by source record.', confirmLabel: 'Import' }))) return
    setImporting(true); setErr(null)
    try {
      for (const table of ['electrical', 'hardware']) {
        let offset = 0, done = false, guard = 0, total = 0, imported = 0
        while (!done && guard++ < 200) {
          const d = await migrateCatalogBatch(table, offset, 10)
          total = d.total; imported += d.imported; offset = d.nextOffset; done = d.done
          setImportMsg(`${table}: ${Math.min(offset, total)}/${total}`)
        }
      }
      setImportMsg('Import complete.'); loadCatalog()
    } catch (e) { setErr(e.message || String(e)) } finally { setImporting(false) }
  }
  async function removeCatalog(it) { if (!(await confirmDialog({ title: 'Delete catalog item?', message: `Remove "${it.name}" from the catalog?`, confirmLabel: 'Delete', danger: true }))) return; run(() => deleteCatalogItem(it.id)).then(loadCatalog) }

  if (!open) return null

  const btn = (primary) => ({ padding: '6px 12px', fontFamily: sans, fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, background: primary ? accent : 'none', color: primary ? '#fff' : textPrim, border: primary ? 'none' : `0.5px solid ${border}` })
  const iconBtn = { padding: '2px 7px', fontFamily: sans, fontSize: 12, background: 'none', border: `0.5px solid ${border}`, borderRadius: 4, cursor: 'pointer', color: textMut }
  const tabStyle = t => ({ padding: '7px 16px', fontFamily: sans, fontSize: 12, fontWeight: tab === t ? 600 : 400, color: tab === t ? textPrim : textMut, background: 'none', border: 'none', borderBottom: tab === t ? `2px solid ${accent}` : '2px solid transparent', cursor: 'pointer', marginBottom: -1 })
  const railBtn = (active) => ({ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 2, borderRadius: 6, border: 'none', cursor: 'pointer', background: active ? (dark ? 'rgba(255,255,255,0.07)' : 'rgba(7,39,45,0.06)') : 'transparent', fontFamily: sans, fontSize: 12.5, fontWeight: active ? 600 : 500, color: textPrim })

  const typeRow = types.find(t => t.schedule_type === activeType)
  const fields = fieldsByType[activeType] || []
  const setVals = optionsBySet[activeSet] || []

  return (
    <div data-modal-id="ScheduleLibraryModal" style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(7,39,45,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: bg, borderRadius: 12, border: `0.5px solid ${border}`, boxShadow: '0 8px 40px rgba(0,0,0,0.22)', width: '100%', maxWidth: 1100, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderBottom: `0.5px solid ${border}`, background: panel, flexShrink: 0 }}>
        <div style={{ fontFamily: display, fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: textPrim }}>Schedule Library</div>
        <div style={{ fontFamily: sans, fontSize: 12, color: textMut }}>Master vocabulary, columns &amp; catalog for all schedules</div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setOpen(false)} title="Close (Esc)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMut, fontSize: 22, lineHeight: 1 }}>×</button>
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '0 18px', borderBottom: `1px solid ${border}`, background: panel, flexShrink: 0 }}>
        <button style={tabStyle('columns')} onClick={() => setTab('columns')}>Columns</button>
        <button style={tabStyle('options')} onClick={() => setTab('options')}>Option Lists</button>
        <button style={tabStyle('catalog')} onClick={() => setTab('catalog')}>Catalog</button>
      </div>

      {err && <div style={{ padding: '6px 18px', color: '#c0392b', fontFamily: sans, fontSize: 12, background: dark ? 'rgba(192,57,43,0.12)' : '#fdecea' }}>{err}</div>}
      {!cfg && <div style={{ padding: 24, fontFamily: sans, fontSize: 13, color: textMut }}>Loading…</div>}

      {cfg && tab === 'columns' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ width: 210, flexShrink: 0, borderRight: `0.5px solid ${border}`, overflowY: 'auto', background: panel, padding: '10px 8px' }}>
            <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: textMut, padding: '4px 8px 8px' }}>Schedule types</div>
            {types.map(t => <button key={t.schedule_type} style={railBtn(t.schedule_type === activeType)} onClick={() => setActiveType(t.schedule_type)}>{t.schedule_type}</button>)}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontFamily: display, fontSize: 15, fontWeight: 700, color: textPrim }}>{activeType}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: mono, fontSize: 10, color: textMut }}>Group by</span>
                <select value={typeRow?.group_by || 'none'} onChange={e => setGroupBy(e.target.value)} disabled={busy} style={{ fontFamily: sans, fontSize: 12, padding: '4px 6px', borderRadius: 4, border: `0.5px solid ${border}`, background: panel, color: textPrim }}>
                  {GROUP_BYS.map(g => <option key={g} value={g}>{g === 'spec_section' ? 'Spec section' : g === 'room_string' ? 'Room' : 'None'}</option>)}
                </select>
                <button style={btn(true)} disabled={busy} onClick={addField}>+ Add column</button>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Order', 'Column label', 'Stored key', 'Kind', 'Option list', 'In grid', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontFamily: mono, fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase', color: textMut, borderBottom: `0.5px solid ${border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr key={f.id} style={{ borderBottom: `0.5px solid ${border}` }}>
                    <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                      <button style={iconBtn} disabled={busy || i === 0} onClick={() => moveField(fields, i, -1)}>↑</button>{' '}
                      <button style={iconBtn} disabled={busy || i === fields.length - 1} onClick={() => moveField(fields, i, 1)}>↓</button>
                    </td>
                    <td style={{ padding: '5px 8px', fontFamily: sans, fontSize: 12.5, color: textPrim, cursor: 'pointer' }} onClick={() => editFieldLabel(f)} title="Rename">{f.label}</td>
                    <td style={{ padding: '5px 8px', fontFamily: mono, fontSize: 11, color: textMut }}>{f.col_key}</td>
                    <td style={{ padding: '5px 8px' }}>
                      <select value={f.kind} disabled={busy} onChange={e => setFieldKind(f, e.target.value)} style={{ fontFamily: sans, fontSize: 12, padding: '2px 4px', borderRadius: 4, border: `0.5px solid ${border}`, background: panel, color: textPrim }}>
                        {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      <select value={f.option_set || ''} disabled={busy} onChange={e => setFieldOptionSet(f, e.target.value)} style={{ fontFamily: sans, fontSize: 12, padding: '2px 4px', borderRadius: 4, border: `0.5px solid ${border}`, background: panel, color: f.option_set ? textPrim : textMut, maxWidth: 180 }}>
                        <option value="">— none (free text) —</option>
                        {optionSets.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}><input type="checkbox" checked={f.in_table !== false} disabled={busy} onChange={() => toggleInTable(f)} /></td>
                    <td style={{ padding: '5px 8px', textAlign: 'right' }}><button style={{ ...iconBtn, color: '#c0392b' }} disabled={busy} onClick={() => removeField(f)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 10, fontFamily: sans, fontSize: 11, color: textMut }}>Set a column's <strong>Kind</strong> to <em>select</em> and choose an <strong>Option list</strong> to constrain it to fixed values. Changes show up in the Schedule Builder.</div>
          </div>
        </div>
      )}

      {cfg && tab === 'options' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ width: 230, flexShrink: 0, borderRight: `0.5px solid ${border}`, overflowY: 'auto', background: panel, padding: '10px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 8px' }}>
              <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: textMut }}>Option lists</span>
              <button style={{ ...iconBtn, fontSize: 11 }} disabled={busy} onClick={addSet}>+ New</button>
            </div>
            {optionSets.map(s => <button key={s} style={railBtn(s === activeSet)} onClick={() => setActiveSet(s)}>{s} <span style={{ color: textMut, fontWeight: 400 }}>· {(optionsBySet[s] || []).length}</span></button>)}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontFamily: display, fontSize: 15, fontWeight: 700, color: textPrim }}>{activeSet}</div>
              <button style={btn(true)} disabled={busy} onClick={addVal}>+ Add value</button>
            </div>
            {setVals.map((o, i) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: rowBg, borderRadius: 5, marginBottom: 3 }}>
                <button style={iconBtn} disabled={busy || i === 0} onClick={() => moveVal(setVals, i, -1)}>↑</button>
                <button style={iconBtn} disabled={busy || i === setVals.length - 1} onClick={() => moveVal(setVals, i, 1)}>↓</button>
                <span style={{ flex: 1, fontFamily: sans, fontSize: 12.5, color: textPrim }}>{o.value}</span>
                <button style={iconBtn} disabled={busy} onClick={() => renameVal(o)}>Rename</button>
                <button style={{ ...iconBtn, color: '#c0392b' }} disabled={busy} onClick={() => removeVal(o)}>Delete</button>
              </div>
            ))}
            {setVals.length === 0 && <div style={{ fontFamily: sans, fontSize: 12, color: textMut }}>No values yet.</div>}
          </div>
        </div>
      )}

      {cfg && tab === 'catalog' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ width: 210, flexShrink: 0, borderRight: `0.5px solid ${border}`, overflowY: 'auto', background: panel, padding: '10px 8px' }}>
            <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: textMut, padding: '4px 8px 8px' }}>Schedule types</div>
            {types.map(t => <button key={t.schedule_type} style={railBtn(t.schedule_type === activeType)} onClick={() => setActiveType(t.schedule_type)}>{t.schedule_type}</button>)}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontFamily: display, fontSize: 15, fontWeight: 700, color: textPrim }}>{activeType} catalog</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {importMsg && <span style={{ fontFamily: mono, fontSize: 11, color: textMut }}>{importMsg}</span>}
                <button style={btn(false)} disabled={importing} onClick={doImport}>{importing ? 'Importing…' : 'Import from Airtable'}</button>
              </div>
            </div>
            <div style={{ fontFamily: sans, fontSize: 11.5, color: textMut, marginBottom: 12, maxWidth: 640 }}>
              Standard products designers can pull into a project schedule (fields auto-fill, then override per project).
              Import brings over the Electrical Fixture and Hardware type templates from Airtable. Other types can be built up here over time.
            </div>
            {catalog === null && <div style={{ fontFamily: sans, fontSize: 12, color: textMut }}>Loading…</div>}
            {Array.isArray(catalog) && catalog.length === 0 && <div style={{ fontFamily: sans, fontSize: 12, color: textMut }}>No catalog products for {activeType} yet.</div>}
            {Array.isArray(catalog) && catalog.map(it => {
              const img = it.image_url
              const summary = Object.entries(it.attrs || {}).filter(([, v]) => v).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ')
              return (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: rowBg, borderRadius: 6, marginBottom: 4 }}>
                  {img ? <img src={img} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4, border: `0.5px solid ${border}` }} /> : <div style={{ width: 34, height: 34, borderRadius: 4, border: `0.5px dashed ${border}` }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: sans, fontSize: 12.5, fontWeight: 600, color: textPrim }}>{it.name}</div>
                    {summary && <div style={{ fontFamily: sans, fontSize: 11, color: textMut, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</div>}
                  </div>
                  <button style={{ ...iconBtn, color: '#c0392b' }} disabled={busy} onClick={() => removeCatalog(it)}>Delete</button>
                </div>
              )
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
